(function () {
  if (customElements.get('hm-carousel')) return;

  class HmCarousel extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-hm-car-viewport]');
      this.track = this.querySelector('[data-hm-car-track]');
      if (!this.viewport || !this.track) return;

      this.slides = Array.from(this.querySelectorAll('[data-hm-car-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-hm-car-dot]'));
      this.prevBtn = this.querySelector('[data-hm-car-prev]');
      this.nextBtn = this.querySelector('[data-hm-car-next]');
      this.realCount = this.slides.length;
      this.timer = null;
      this.raf = 0;
      this.animRaf = 0;
      this.lock = false;
      this.x = 0;
      this.dragStartXpos = 0;
      this.dragging = false;
      this.dragPending = false;
      this.moved = false;
      this.animating = false;
      this.autoplay = this.getAttribute('data-autoplay') === 'true' && this.realCount > 1;
      this.mode = this.getAttribute('data-autoplay-mode') === 'continuous' ? 'continuous' : 'paused';
      this.interval = Math.max(2000, Number(this.getAttribute('data-interval')) || 5000);
      this.pauseHover = this.getAttribute('data-pause-hover') !== 'false';
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      this.classList.toggle('is-continuous', this.mode === 'continuous');

      this.onPrev = function () {
        this.go(-1);
        this.restart();
      }.bind(this);
      this.onNext = function () {
        this.go(1);
        this.restart();
      }.bind(this);
      this.onEnter = this.stop.bind(this);
      this.onLeave = this.start.bind(this);
      this.onVis = this.onVisibility.bind(this);
      this.onMouseDown = this.mouseDown.bind(this);
      this.onMouseMove = this.mouseMove.bind(this);
      this.onMouseUp = this.mouseUp.bind(this);
      this.onTouchStart = this.touchStart.bind(this);
      this.onTouchMove = this.touchMove.bind(this);
      this.onTouchEnd = this.touchEnd.bind(this);
      this.onClickCapture = this.clickCapture.bind(this);
      this.onDragStart = function (event) {
        event.preventDefault();
      };

      if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext);
      this.dots.forEach(
        function (dot, i) {
          dot.addEventListener(
            'click',
            function () {
              this.goTo(i);
              this.restart();
            }.bind(this)
          );
        }.bind(this)
      );

      if (this.autoplay && this.pauseHover && !this.reduceMotion && this.finePointer) {
        this.addEventListener('mouseenter', this.onEnter);
        this.addEventListener('mouseleave', this.onLeave);
        this.addEventListener('focusin', this.onEnter);
        this.addEventListener('focusout', this.onLeave);
      }
      document.addEventListener('visibilitychange', this.onVis);
      this.viewport.addEventListener('mousedown', this.onMouseDown);
      this.viewport.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.viewport.addEventListener('touchmove', this.onTouchMove, { passive: false });
      this.viewport.addEventListener('touchend', this.onTouchEnd);
      this.viewport.addEventListener('touchcancel', this.onTouchEnd);
      this.viewport.addEventListener('click', this.onClickCapture, true);
      this.viewport.addEventListener('dragstart', this.onDragStart);

      this.onTick = this.tickContinuous.bind(this);
      this.onResize = function () {
        this.layoutDirty = true;
        this.wrapIfNeeded();
      }.bind(this);
      window.addEventListener('resize', this.onResize);

      this.apply(0);
      if (this.realCount > 1) {
        this.ensureAfter();
        this.ensureBefore();
      }
      this.syncDots(0);
      if (this.autoplay && !this.reduceMotion) {
        this.onView = function (entries) {
          if (entries[0] && entries[0].isIntersecting) this.restart();
          else this.stop();
        }.bind(this);
        this.viewObserver = new IntersectionObserver(this.onView, { rootMargin: '120px' });
        this.viewObserver.observe(this);
      }
    }

    disconnectedCallback() {
      this.stop();
      if (this.viewObserver) this.viewObserver.disconnect();
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this.onPrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this.onNext);
      this.removeEventListener('mouseenter', this.onEnter);
      this.removeEventListener('mouseleave', this.onLeave);
      this.removeEventListener('focusin', this.onEnter);
      this.removeEventListener('focusout', this.onLeave);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('visibilitychange', this.onVis);
      window.removeEventListener('mousemove', this.onMouseMove);
      window.removeEventListener('mouseup', this.onMouseUp);
      if (this.viewport) {
        this.viewport.removeEventListener('mousedown', this.onMouseDown);
        this.viewport.removeEventListener('touchstart', this.onTouchStart);
        this.viewport.removeEventListener('touchmove', this.onTouchMove);
        this.viewport.removeEventListener('touchend', this.onTouchEnd);
        this.viewport.removeEventListener('touchcancel', this.onTouchEnd);
        this.viewport.removeEventListener('click', this.onClickCapture, true);
        this.viewport.removeEventListener('dragstart', this.onDragStart);
      }
    }

    readLayout() {
      var style = getComputedStyle(this);
      var gap = parseFloat(style.getPropertyValue('--hm-car-gap'));
      var per = Number(style.getPropertyValue('--hm-car-per')) || 1;
      var slide = this.track.querySelector('[data-hm-car-slide]');
      var after = this.track.querySelector('[data-clone="after"]');
      this.cachedGap = isNaN(gap) ? 0 : gap;
      this.cachedBuffer = Math.min(this.realCount, Math.max(2, Math.ceil(per) + 1));
      this.cachedUnit = slide ? slide.offsetWidth + this.cachedGap : 200;
      this.cachedStart = slide ? slide.offsetLeft - this.track.offsetLeft : 0;
      this.cachedPeriod = slide && after ? after.offsetLeft - slide.offsetLeft : 0;
      this.layoutDirty = false;
    }

    ensureLayout() {
      if (this.layoutDirty !== false) this.readLayout();
    }

    gap() {
      this.ensureLayout();
      return this.cachedGap;
    }

    buffer() {
      this.ensureLayout();
      return this.cachedBuffer;
    }

    originals() {
      return Array.from(this.track.querySelectorAll('[data-hm-car-slide]'));
    }

    leftOf(el) {
      return el.offsetLeft - this.track.offsetLeft;
    }

    view() {
      return -this.x;
    }

    apply(x) {
      this.x = x;
      this.track.style.transform = 'translate3d(' + x + 'px, 0, 0)';
    }

    animateToView(targetView) {
      var from = this.x;
      var to = -targetView;
      if (this.reduceMotion || Math.abs(to - from) < 0.5) {
        this.apply(to);
        this.animating = false;
        this.wrapIfNeeded();
        this.syncDots(this.currentIndex());
        return;
      }
      if (this.animRaf) window.cancelAnimationFrame(this.animRaf);
      this.animating = true;
      var start = performance.now();
      var dur = 380;
      var tick = function (now) {
        var t = Math.min(1, (now - start) / dur);
        var ease = 1 - Math.pow(1 - t, 3);
        this.apply(from + (to - from) * ease);
        if (t < 1) {
          this.animRaf = window.requestAnimationFrame(tick);
        } else {
          this.animRaf = 0;
          this.animating = false;
          this.wrapIfNeeded();
          this.syncDots(this.currentIndex());
        }
      }.bind(this);
      this.animRaf = window.requestAnimationFrame(tick);
    }

    cloneSlide(slide, where) {
      var clone = slide.cloneNode(true);
      clone.removeAttribute('data-hm-car-slide');
      clone.setAttribute('data-clone', where);
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('[id]').forEach(function (node) {
        node.removeAttribute('id');
      });
      return clone;
    }

    loopPeriod() {
      this.ensureLayout();
      return this.cachedPeriod || 0;
    }

    loopStart() {
      this.ensureLayout();
      return this.cachedStart || 0;
    }

    ensureAfter() {
      if (this.realCount < 2 || this.track.querySelector('[data-clone="after"]')) return;
      var count = this.buffer();
      var slides = this.originals();
      for (var i = 0; i < count; i++) {
        this.track.appendChild(this.cloneSlide(slides[i], 'after'));
      }
      this.layoutDirty = true;
    }

    ensureBefore() {
      if (this.realCount < 2 || this.track.querySelector('[data-clone="before"]')) return;
      var count = this.buffer();
      var slides = this.originals();
      var extra = 0;
      for (var i = slides.length - count; i < slides.length; i++) {
        var clone = this.cloneSlide(slides[i], 'before');
        this.track.insertBefore(clone, this.track.firstChild);
        extra += clone.offsetWidth + this.gap();
      }
      this.layoutDirty = true;
      this.apply(this.x - extra);
      if (this.dragging) this.dragStartXpos -= extra;
    }

    ensureEdges() {
      this.ensureBefore();
      this.ensureAfter();
      this.wrapIfNeeded();
    }

    removeAfter() {
      this.track.querySelectorAll('[data-clone="after"]').forEach(function (node) {
        node.remove();
      });
    }

    removeBefore() {
      this.track.querySelectorAll('[data-clone="before"]').forEach(function (node) {
        node.remove();
      });
    }

    nearestSlide() {
      var view = this.view();
      var nearest = this.track.children[0];
      var best = Infinity;
      Array.from(this.track.children).forEach(
        function (el) {
          var d = Math.abs(this.leftOf(el) - view);
          if (d < best) {
            best = d;
            nearest = el;
          }
        }.bind(this)
      );
      return nearest;
    }

    currentIndex() {
      var view = this.view();
      var best = 0;
      var bestDist = Infinity;
      Array.from(this.track.children).forEach(
        function (el) {
          var dist = Math.abs(this.leftOf(el) - view);
          if (dist < bestDist) {
            bestDist = dist;
            var idx = el.getAttribute('data-hm-index');
            if (idx != null) best = Number(idx);
          }
        }.bind(this)
      );
      return best;
    }

    wrapIfNeeded() {
      if (this.realCount < 2) return;
      var period = this.loopPeriod();
      if (period < 1) return;
      var start = this.loopStart();
      var view = this.view();
      var guard = 0;
      while (view >= start + period && guard < 4) {
        this.apply(this.x + period);
        view = this.view();
        guard += 1;
      }
      while (view < start && guard < 4) {
        this.apply(this.x - period);
        view = this.view();
        guard += 1;
      }
    }

    go(step) {
      if (this.realCount < 2 || this.animating) return;
      if (step > 0) this.ensureAfter();
      if (step < 0) this.ensureBefore();
      var current = this.nearestSlide();
      var target = step > 0 ? current.nextElementSibling : current.previousElementSibling;
      if (!target) return;
      this.animateToView(this.leftOf(target));
    }

    goTo(index) {
      var slides = this.originals();
      var slide = slides[index];
      if (!slide) return;
      this.animateToView(this.leftOf(slide));
    }

    beginDrag(clientX, clientY) {
      if (this.animRaf) {
        window.cancelAnimationFrame(this.animRaf);
        this.animRaf = 0;
      }
      this.animating = false;
      this.dragPending = true;
      this.dragging = false;
      this.moved = false;
      this.dragStartClientX = clientX;
      this.dragStartClientY = clientY;
      this.dragStartXpos = this.x;
    }

    moveDrag(clientX, clientY, event) {
      if (!this.dragPending && !this.dragging) return false;
      var dx = clientX - this.dragStartClientX;
      var dy = clientY - this.dragStartClientY;
      if (!this.dragging) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return false;
        if (Math.abs(dy) > Math.abs(dx) * 1.2) {
          this.dragPending = false;
          return false;
        }
        this.dragging = true;
        this.dragPending = false;
        this.stop();
        this.viewport.classList.add('is-dragging');
      }
      this.moved = Math.abs(dx) > 4;
      this.apply(this.dragStartXpos + dx);
      this.ensureEdges();
      if (event) event.preventDefault();
      return true;
    }

    endDrag() {
      this.dragPending = false;
      if (!this.dragging) return;
      this.dragging = false;
      this.viewport.classList.remove('is-dragging');
      if (this.mode === 'paused') {
        var nearest = this.nearestSlide();
        if (nearest) this.animateToView(this.leftOf(nearest));
        else this.wrapIfNeeded();
      } else {
        this.wrapIfNeeded();
      }
      this.syncDots(this.currentIndex());
      this.restart();
    }

    mouseDown(event) {
      if (event.button !== 0) return;
      if (event.target.closest('button')) return;
      this.beginDrag(event.clientX, event.clientY);
      window.addEventListener('mousemove', this.onMouseMove);
      window.addEventListener('mouseup', this.onMouseUp);
    }

    mouseMove(event) {
      this.moveDrag(event.clientX, event.clientY, event);
    }

    mouseUp() {
      window.removeEventListener('mousemove', this.onMouseMove);
      window.removeEventListener('mouseup', this.onMouseUp);
      this.endDrag();
    }

    touchStart(event) {
      if (event.touches.length !== 1) return;
      if (event.target.closest('button')) return;
      this.beginDrag(event.touches[0].clientX, event.touches[0].clientY);
    }

    touchMove(event) {
      if (event.touches.length !== 1) return;
      this.moveDrag(event.touches[0].clientX, event.touches[0].clientY, event);
    }

    touchEnd() {
      this.endDrag();
    }

    clickCapture(event) {
      if (!this.moved) return;
      event.preventDefault();
      event.stopPropagation();
      this.moved = false;
    }

    tickContinuous(now) {
      if (!this.playingContinuous) return;
      if (!this.lastTick) this.lastTick = now;
      var dt = Math.min(48, now - this.lastTick);
      this.lastTick = now;
      this.ensureLayout();
      var unit = this.cachedUnit > 1 ? this.cachedUnit : 200;
      var next = this.x - (unit / this.interval) * dt;
      var period = this.cachedPeriod;
      if (period > 1) {
        var view = -next;
        if (view >= this.cachedStart + period) next += period;
        else if (view < this.cachedStart) next -= period;
      }
      this.apply(next);
      this.raf = window.requestAnimationFrame(this.onTick);
    }

    syncDots(index) {
      this.dots.forEach(function (dot, idx) {
        var on = idx === index;
        dot.classList.toggle('is-active', on);
        dot.setAttribute('aria-current', on ? 'true' : 'false');
      });
      if (this.prevBtn) this.prevBtn.disabled = this.realCount <= 1;
      if (this.nextBtn) this.nextBtn.disabled = this.realCount <= 1;
    }

    start() {
      this.stop();
      if (!this.autoplay || this.reduceMotion || this.realCount < 2 || this.dragging) return;
      if (this.mode === 'continuous') {
        this.layoutDirty = true;
        this.playingContinuous = true;
        this.lastTick = 0;
        this.raf = window.requestAnimationFrame(this.onTick);
        return;
      }
      this.timer = window.setInterval(this.go.bind(this, 1), this.interval);
    }

    stop() {
      this.playingContinuous = false;
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
      if (this.raf) {
        window.cancelAnimationFrame(this.raf);
        this.raf = 0;
      }
    }

    restart() {
      if (this.autoplay && !this.reduceMotion && !this.dragging) this.start();
    }

    onVisibility() {
      if (document.hidden) this.stop();
      else this.restart();
    }
  }

  customElements.define('hm-carousel', HmCarousel);
})();
